import { useState } from 'react';
import { Modal, Form, Input, Button, Alert, message } from 'antd';
import { CopyOutlined, KeyOutlined } from '@ant-design/icons';
import apiClient from '@/api/client';
import { useT } from '@/i18n';

interface ApiKeyModalProps {
  open: boolean;
  onClose: () => void;
}

interface GenerateKeyResponse {
  key: string;
  name: string;
  created_at: string;
  warning: string;
}

const ApiKeyModal = ({ open, onClose }: ApiKeyModalProps) => {
  const [form] = Form.useForm();
  const { t } = useT();
  const [loading, setLoading] = useState(false);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [keyName, setKeyName] = useState<string>('');

  const handleGenerate = async (values: { name?: string }) => {
    setLoading(true);
    try {
      const response = await apiClient.post<GenerateKeyResponse>(
        '/auth/users/generate-api-key/',
        { name: values.name || '' }
      );

      setGeneratedKey(response.data.key);
      setKeyName(response.data.name);
      message.success(t('API Key 生成成功，请立即复制保存'));
    } catch (error: any) {
      message.error(error.response?.data?.error || t('API Key 生成失败'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (generatedKey) {
      navigator.clipboard.writeText(generatedKey);
      message.success(t('已复制到剪贴板'));
    }
  };

  const handleClose = () => {
    form.resetFields();
    setGeneratedKey(null);
    setKeyName('');
    onClose();
  };

  return (
    <Modal
      title={<><KeyOutlined /> {t('生成 API Key')}</>}
      open={open}
      onCancel={handleClose}
      footer={null}
      width={700}
    >
      {!generatedKey ? (
        <Form form={form} onFinish={handleGenerate} layout="vertical" className="mt-4">
          <Alert
            title={t('用途说明')}
            description={t('API Key 用于在 Python/Bash 脚本中调用 BSP 外部接口，如文件下载接口。生成后请妥善保管，不要泄露给他人。')}
            type="info"
            showIcon
            className="mb-4"
          />

          <Form.Item
            label={t('Key 名称（可选）')}
            name="name"
            tooltip={t('为 Key 设置一个易识别的名称，留空则自动生成时间戳名称')}
          >
            <Input placeholder={t('例如：生产环境脚本使用')} maxLength={100} />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              {t('生成 API Key')}
            </Button>
          </Form.Item>
        </Form>
      ) : (
        <div className="mt-4">
          <Alert
            title={t('⚠️ 请立即复制保存')}
            description={t('此 Key 仅显示一次，关闭后将无法再次查看完整密钥。请复制到安全的地方妥善保管。')}
            type="warning"
            showIcon
            className="mb-4"
          />

          <div className="mb-4">
            <span className="font-semibold">{t('Key 名称：')}</span>
            <span className="ml-2">{keyName}</span>
          </div>

          <div className="bg-gray-50 p-4 rounded border mb-4">
            <div className="flex items-start justify-between">
              <code className="text-sm font-mono break-all flex-1 mr-2">{generatedKey}</code>
              <Button
                size="small"
                icon={<CopyOutlined />}
                onClick={handleCopy}
                title={t('复制 Key')}
              />
            </div>
          </div>

          <Alert
            title={t('Python 使用示例')}
            description={
              <pre className="mt-2 text-xs bg-white p-2 rounded border overflow-x-auto">
{`import requests

headers = {"X-API-Key": "${generatedKey}"}
response = requests.get(
    "https://bsp.momenta.works/api/v1/external/deliverables/download/123/?type=external_release",
    headers=headers
)`}
              </pre>
            }
            type="info"
            showIcon
          />

          <div className="mt-4 flex gap-2">
            <Button icon={<CopyOutlined />} onClick={handleCopy}>
              {t('复制 Key')}
            </Button>
            <Button type="primary" onClick={handleClose}>
              {t('已保存，关闭')}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ApiKeyModal;
